import { Extension } from "@tiptap/core";
import { DOMParser as PMDOMParser, DOMSerializer, Slice } from "@tiptap/pm/model";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import type { EditorView } from "@tiptap/pm/view";

import type { ReactionResourceOption } from "../suggestion/suggestion-types";
import {
	decodeResourceSlotToken,
	parseResourceSlotsAttr,
} from "./paragraph-resource-meta";

export interface ResourceSlotsClipboardOptions {
  enabled: boolean;
  onPastedSlots?: (slots: string[]) => void;
}

/** Maps pasted slot tokens to known resource options for picked-resource registration. */
export function resolvePastedSlotsToResources(
	slots: string[],
	availableResources: ReactionResourceOption[],
): ReactionResourceOption[] {
	const seen = new Set<string>();
	const resolved: ReactionResourceOption[] = [];

	for (const token of slots) {
		const decoded = decodeResourceSlotToken(token);
		if (!decoded) {
			continue;
		}

		const braceInner = decoded.braceInner.trim();
		let match: ReactionResourceOption | undefined;

		if (decoded.resourceType === "screenField") {
			match = availableResources.find(
				(r) => r.resourceType === "screenField" && String(r.value) === braceInner,
			);
		} else if (decoded.resourceType === "advancedTrigger") {
			match = availableResources.find(
				(r) =>
					r.resourceType === "advancedTrigger" &&
          r.triggerName === braceInner,
			);
		} else {
			match = availableResources.find(
				(r) =>
					r.resourceType === decoded.resourceType &&
          (r.label.trim() === braceInner || String(r.value) === braceInner),
			);
		}

		if (!match) {
			continue;
		}

		const key = `${match.resourceType}:${match.value}`;
		if (seen.has(key)) {
			continue;
		}
		seen.add(key);
		resolved.push(match);
	}

	return resolved;
}

function collectSlotsFromDocContent(content: Slice["content"]): string[] {
	const slots: string[] = [];
	content.forEach((node) => {
		if (node.type.name !== "paragraph") {
			return;
		}
		const raw = node.attrs.resourceSlots as string | null;
		if (raw) {
			slots.push(...parseResourceSlotsAttr(raw));
		}
	});
	return [...new Set(slots)];
}

function serializeSelectionHtml(view: EditorView): string | null {
	const { state } = view;
	if (state.selection.empty) {
		return null;
	}

	const slice = state.selection.content();
	const serializer = DOMSerializer.fromSchema(state.schema);
	const wrap = document.createElement("div");
	wrap.appendChild(serializer.serializeFragment(slice.content, { document: window.document }));
	return wrap.innerHTML;
}

function writeSelectionToClipboard(view: EditorView, event: ClipboardEvent, isCut: boolean): boolean {
	const { state } = view;
	if (state.selection.empty || !event.clipboardData) {
		return false;
	}

	const html = serializeSelectionHtml(view);
	if (!html) {
		return false;
	}

	const slice = state.selection.content();
	const text = slice.content.textBetween(0, slice.content.size, "\n\n");

	event.clipboardData.clearData();
	event.clipboardData.setData("text/html", html);
	event.clipboardData.setData("text/plain", text);

	if (isCut) {
		view.dispatch(state.tr.deleteSelection());
	}

	event.preventDefault();
	return true;
}

function pasteResourceSlotsHtml(
	view: EditorView,
	html: string,
	onPastedSlots?: (slots: string[]) => void,
): boolean {
	if (!html.includes("data-resource-slots")) {
		return false;
	}

	const wrap = document.createElement("div");
	wrap.innerHTML = html;

	const parser = PMDOMParser.fromSchema(view.state.schema);
	const doc = parser.parse(wrap);
	if (!doc.content.size) {
		return false;
	}

	const slice = new Slice(doc.content, 0, 0);
	view.dispatch(view.state.tr.replaceSelection(slice).scrollIntoView());

	const slots = collectSlotsFromDocContent(doc.content);
	if (slots.length) {
		onPastedSlots?.(slots);
	}

	return true;
}

/** Copy/paste reaction HTML with paragraph `data-resource-slots` intact. */
export const ResourceSlotsClipboard = Extension.create<ResourceSlotsClipboardOptions>({
	name: "resourceSlotsClipboard",

	addOptions() {
		return {
			enabled: false,
			onPastedSlots: undefined,
		};
	},

	addProseMirrorPlugins() {
		const extension = this;
		if (!extension.options.enabled) {
			return [];
		}

		return [
			new Plugin({
				key: new PluginKey("resourceSlotsClipboard"),
				props: {
					handlePaste(view, event) {
						const html = event.clipboardData?.getData("text/html");
						if (!html) {
							return false;
						}

						const handled = pasteResourceSlotsHtml(
							view,
							html,
							extension.options.onPastedSlots,
						);
						if (handled) {
							event.preventDefault();
						}
						return handled;
					},
					handleDOMEvents: {
						copy(view, event) {
							return writeSelectionToClipboard(view, event, false);
						},
						cut(view, event) {
							return writeSelectionToClipboard(view, event, true);
						},
					},
				},
			}),
		];
	},
});
